import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, X, MapPin, Users, Home, Shield, Building, Calendar, Clock, Trash2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { usePropertyComparison } from '@/hooks/usePropertyComparison';

const Compare = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { comparisonList, removeFromComparison, clearComparison } = usePropertyComparison();

  const getPriceClass = (price: number) => {
    if (price <= 1500) return 'text-green-600';
    if (price <= 4000) return 'text-orange-600';
    return 'text-red-600';
  };

  const getDistanceClass = (distance: number) => {
    if (distance <= 1) return 'text-green-600';
    if (distance <= 3) return 'text-orange-600';
    return 'text-red-600';
  };

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: { en: 'Private Room', ar: 'غرفة خاصة' },
      shared: { en: 'Shared Room', ar: 'غرفة مشتركة' },
      entire: { en: 'Entire Unit', ar: 'وحدة كاملة' },
      apartment: { en: 'Entire Unit', ar: 'وحدة كاملة' }
    };
    return language === 'ar' ? labels[type as keyof typeof labels]?.ar : labels[type as keyof typeof labels]?.en;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      mixed: { en: 'Mixed', ar: 'مختلط' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' },
      any: { en: 'Mixed', ar: 'مختلط' }
    };
    return language === 'ar' ? labels[gender as keyof typeof labels]?.ar : labels[gender as keyof typeof labels]?.en;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (comparisonList.length === 0) {
    return (
      <div className={`min-h-screen flex flex-col ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Header />
        <div className="flex-1 container mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'مقارنة العقارات' : 'Compare Properties'}
            </h1>
          </div>

          <div className="text-center py-12">
            <Building className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-medium mb-2">
              {language === 'ar' ? 'لا توجد عقارات للمقارنة' : 'No Properties to Compare'}
            </h2>
            <p className="text-muted-foreground mb-2">
              {language === 'ar' ? 'أضف عقارات للمقارنة لمشاهدة الفروقات بينها' : 'Add properties to comparison to see the differences'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {language === 'ar' ? '(الحد الأقصى 3 عقارات)' : '(Maximum 3 properties)'}
            </p>
            <Button 
              onClick={() => navigate('/search')}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {language === 'ar' ? 'ابحث عن عقارات' : 'Search Properties'}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'مقارنة العقارات' : 'Compare Properties'}
            </h1>
            <Badge variant="secondary">
              {comparisonList.length}/3 {language === 'ar' ? 'عقارات' : 'properties'}
            </Badge>
          </div>
          <Button variant="outline" onClick={clearComparison}>
            <Trash2 className="h-4 w-4 mr-2" />
            {language === 'ar' ? 'مسح الكل' : 'Clear All'}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {language === 'ar' ? 'مقارنة العقارات' : 'Property Comparison'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">
                      {language === 'ar' ? 'العقار' : 'Property'}
                    </TableHead>
                    {comparisonList.map((property) => (
                      <TableHead key={property.id} className="min-w-[250px] relative">
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">
                            {property.title}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 ml-2 flex-shrink-0"
                            onClick={() => removeFromComparison(property.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Images */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'الصورة' : 'Image'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <div className="relative h-32 w-full rounded-lg overflow-hidden">
                          <img
                            src={property.photos?.[0] || '/placeholder.svg'}
                            alt={property.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                          {property.verified && (
                            <Badge className="absolute top-1 right-1 bg-green-500 hover:bg-green-600 text-white text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              {language === 'ar' ? 'مُتحقق' : 'Verified'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Price */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'السعر الشهري' : 'Monthly Price'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <div className={`text-lg font-bold ${getPriceClass(property.price_egp)}`}>
                          {property.price_egp?.toLocaleString()} EGP
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Deposit */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'التأمين' : 'Deposit'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <div className="font-medium">
                          {property.deposit_egp > 0 ? `${property.deposit_egp?.toLocaleString()} EGP` : 
                            language === 'ar' ? 'بدون تأمين' : 'No deposit'
                          }
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* University */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'الجامعة' : 'University'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {language === 'ar' ? property.universities?.name_ar : property.universities?.name_en}
                          </span>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Distance */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'المسافة من الجامعة' : 'Distance from Campus'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <div className={`font-medium ${getDistanceClass(property.distance_km)}`}>
                          {property.distance_km}km
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Unit Type */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'نوع الوحدة' : 'Unit Type'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <Badge variant="outline">
                          {getUnitTypeLabel(property.unit_type)}
                        </Badge>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Gender Rules */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'قواعد الجنس' : 'Gender Rules'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <Badge variant="outline">
                          {getGenderLabel(property.gender_rules)}
                        </Badge>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Furnished */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'مفروش' : 'Furnished'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        {property.furnished ? (
                          <Badge variant="secondary" className="text-green-600">
                            <Home className="h-3 w-3 mr-1" />
                            {language === 'ar' ? 'نعم' : 'Yes'}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            {language === 'ar' ? 'لا' : 'No'}
                          </span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Available From */}
                  {comparisonList.some((property) => property.available_from) && (
                    <TableRow>
                      <TableCell className="font-medium">
                        {language === 'ar' ? 'متاح من' : 'Available From'}
                      </TableCell>
                      {comparisonList.map((property) => (
                        <TableCell key={property.id}>
                          {property.available_from ? (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-primary" />
                              <span className="text-sm">{formatDate(property.available_from)}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {language === 'ar' ? 'غير محدد' : 'Not specified'}
                            </span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  {/* Minimum Lease */}
                  {comparisonList.some((property) => property.lease_min_months) && (
                    <TableRow>
                      <TableCell className="font-medium">
                        {language === 'ar' ? 'الحد الأدنى للإيجار' : 'Minimum Lease'}
                      </TableCell>
                      {comparisonList.map((property) => (
                        <TableCell key={property.id}>
                          {property.lease_min_months ? (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-primary" />
                              <span className="text-sm">
                                {property.lease_min_months} {language === 'ar' ? 'شهر' : 'months'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {language === 'ar' ? 'غير محدد' : 'Not specified'}
                            </span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  {/* Action Buttons */}
                  <TableRow>
                    <TableCell className="font-medium">
                      {language === 'ar' ? 'الإجراءات' : 'Actions'}
                    </TableCell>
                    {comparisonList.map((property) => (
                      <TableCell key={property.id}>
                        <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                          <Building className="h-4 w-4 mr-2" />
                          {language === 'ar' ? 'استأجر الآن' : 'Rent Now'}
                        </Button>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {comparisonList.length < 3 && (
          <div className="mt-6 text-center">
            <p className="text-muted-foreground mb-4">
              {language === 'ar' 
                ? `يمكنك إضافة ${3 - comparisonList.length} عقار آخر للمقارنة`
                : `You can add ${3 - comparisonList.length} more propert${3 - comparisonList.length === 1 ? 'y' : 'ies'} to compare`
              }
            </p>
            <Button variant="outline" onClick={() => navigate('/search')}>
              {language === 'ar' ? 'ابحث عن عقارات' : 'Search Properties'}
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Compare;
